import { CAPABILITIES, isRole, type AuthUser, type Capability } from '@psspl/types';
import { isRecord } from '@psspl/utils';

import { ApiClientError } from './api-error';

/**
 * Runtime checks on the auth endpoints' payloads (`docs/api.md` §4.2–§4.6).
 *
 * The shapes are ratified in `@psspl/types`, but a typed `httpClient.get<T>()` is
 * only a promise about the body, not a check. The session drives what the whole
 * portal renders, so the user object is validated once at the boundary instead of
 * trusted: a missing field or an unknown role is an error, never a default.
 */

function contractError(detail: string): ApiClientError {
  return new ApiClientError({
    code: 'INTERNAL_ERROR',
    message: 'The sign-in service returned an unexpected response. Please try again later.',
    status: 0,
    details: [{ field: 'response', message: detail }],
  });
}

function requireString(record: Record<string, unknown>, key: string): string {
  const value = record[key];

  if (typeof value !== 'string') {
    throw contractError(`user.${key} missing`);
  }

  return value;
}

const KNOWN_CAPABILITIES: ReadonlySet<string> = new Set(CAPABILITIES);

function isCapability(value: unknown): value is Capability {
  return typeof value === 'string' && KNOWN_CAPABILITIES.has(value);
}

export function parseAuthUser(payload: unknown): AuthUser {
  if (!isRecord(payload)) {
    throw contractError('user is not an object');
  }

  const role = payload['role'];

  if (!isRole(role)) {
    throw contractError(`unknown role ${JSON.stringify(role)}`);
  }

  const capabilities = payload['capabilities'];

  if (!Array.isArray(capabilities)) {
    throw contractError('user.capabilities missing');
  }

  const identityBound = payload['identityBound'];

  if (typeof identityBound !== 'boolean') {
    throw contractError('user.identityBound missing');
  }

  return {
    id: requireString(payload, 'id'),
    email: requireString(payload, 'email'),
    firstName: requireString(payload, 'firstName'),
    lastName: requireString(payload, 'lastName'),
    fullName: requireString(payload, 'fullName'),
    role,
    // A capability this build does not know is dropped: it can only unlock UI that
    // does not exist yet, and the server enforces it either way.
    capabilities: capabilities.filter(isCapability),
    identityBound,
  };
}

/** `POST /auth/microsoft` and `POST /auth/refresh` share this body (ADR-29, §4.4). */
export function parseSession(payload: unknown): {
  readonly user: AuthUser;
  readonly accessToken: string;
} {
  if (!isRecord(payload) || typeof payload['accessToken'] !== 'string') {
    throw contractError('accessToken missing');
  }

  return { user: parseAuthUser(payload['user']), accessToken: payload['accessToken'] };
}
